/**
 * 充值逻辑 Hook
 */
import { computed, ref } from 'vue'
import type { Book } from '../configs/types'
import { PRODUCT_LIST_API, PaymentMethodEnum } from '../constants'
import {
  createAvailablePaymentMethods,
  createSelectPaymentMethod,
  getPayType,
  getPaymentChannelName,
  getTopupPaymentApi,
  initializePaymentMethod,
} from './paymentUtils'
import { api } from '@/api'
import { $formatErrorMessage } from '@/helpers'
import { useLoadingStore, useToastStore } from '@/stores'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { useAxonBeginCheckout } from '@/composables/axon/useAxonBeginCheckout'
import { useFbqInitiateCheckout } from '@/composables/fbq/useFbqInitiateCheckout'

export function usePurchase(Scenario: string, book?: Book) {
  const data = ref<any>({})
  const PayTemplateSuccess = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // 多支付方式相关状态
  const currentPaymentMethod = ref<number>(PaymentMethodEnum.PAYPAL) // 默认 PayPal
  const productLists = ref<any[]>([]) // 按支付方式分组的商品列表
  const hasPayPalPasswordlessPayment = ref(false)
  const hasPlacedOrder = ref(false)

  const { $toast } = useToastStore()
  const { $showLoading, $hideLoading } = useLoadingStore()
  const { elementClickForPay } = useElementClick()
  const { reportBeginCheckout } = useAxonBeginCheckout()
  const { reportInitiateCheckout } = useFbqInitiateCheckout()

  /**
   * 根据支付方式和商品类型获取支付 API
   */
  function getPaymentApi(paymentMethod: number): string {
    return getTopupPaymentApi(paymentMethod)
  }

  /**
   * 初始化支付方式
   */
  function initPaymentMethod() {
    initializePaymentMethod(
      currentPaymentMethod,
      productLists,
      hasPayPalPasswordlessPayment,
      hasPlacedOrder,
    )
  }

  /**
   * 可用的支付方式列表（充值页面排除 Stripe）
   */
  const availablePaymentMethods = computed(createAvailablePaymentMethods(
    productLists,
    hasPayPalPasswordlessPayment,
    hasPlacedOrder,
  ))
  /**
   * 当前支付方式的商品列表（充值商品，无 cycle 或 cycle=0）
   *
   * 从 productLists 中找到对应支付方式的商品列表，然后过滤出充值商品
   */
  const currentPaymentProducts = computed(() => {
    const currentMethod = Number(currentPaymentMethod.value)

    // 从 productLists 中找到对应支付方式的商品列表
    const productList = availablePaymentMethods.value.find(
      list => Number(list.paymentMethod) === currentMethod,
    )

    if (!productList || !productList.products)
      return []

    const products = productList.products

    // 兜底：如果没有 defaultDisplay=1 的商品（即都为 0），展示所有商品
    return products
  })

  /**
   * templateProducts 兼容旧代码，使用 currentPaymentProducts
   */
  const templateProducts = computed(() => currentPaymentProducts.value)

  const getTopUpList = async function () {
    loading.value = true
    error.value = null
    try {
      const res: any = await api.get<any>(PRODUCT_LIST_API.TOPUP, {
        params: {
          type: book?.contentId ? 1 : 2,
          contentId: book?.contentId || '',
          contentChapterId: book?.contentChapterId || '',
        },
      })
      data.value = res.data || {}

      // 解析多支付方式数据
      if (res.data && typeof res.data === 'object') {
        hasPayPalPasswordlessPayment.value = res.data.hasPayPalPasswordlessPayment || false
        hasPlacedOrder.value = res.data.hasPlacedOrder || false
        productLists.value = res.data.productLists || []

        // 初始化默认支付方式
        initPaymentMethod()
      }
    }
    catch (err) {
      const errorMsg = $formatErrorMessage(err)
      error.value = errorMsg
      $toast(errorMsg)
    }
    loading.value = false
  }

  const isReloadPage = function () {
    // 无数据时的处理逻辑
    const currentUrl = new URL(window.location.href)
    if (book?.contentId) {
      // 在阅读页面时，移除 isShowpopupRecharge 参数后刷新页面，更新章节解锁状态
      currentUrl.searchParams.delete('isShowpopupRecharge')
      window.location.href = currentUrl.href
    }
    else {
      window.location.reload()
    }
  }

  const PayTemplate = async function (item: any) {
    elementClickForPay('purchase', {
      purchaseId: item.payConfigId,
      commodityId: item.productId,
      purchaseType: '1',
      price: item.price,
      paymentChannel: getPaymentChannelName(currentPaymentMethod.value),
      purchaseScenario: Scenario, // store chapter
    })

    $showLoading()
    const url = `${window.location.origin}/paying?redirect_source=${encodeURIComponent(window.location.href)}&paymentChannel=${getPaymentChannelName(currentPaymentMethod.value)}&axon_price=${item.price}&axon_product_id=${item.productId}`
    const fbEventId = reportInitiateCheckout({
      value: item.price,
      currency: 'USD',
      contentIds: [String(item.productId)],
      numItems: 1,
    })
    const params = {
      payConfigId: item.payConfigId,
      productId: item.productId,
      contentId: book?.contentId,
      contentChapterId: book?.contentChapterId,
      memberCurrencyCode: 'usd',
      payType: getPayType(currentPaymentMethod.value),
      type: book?.contentId ? 1 : 2, // 1: 阅读页面, 2: store
      successUrl: `${url}&payment_status=success`,
      cancelUrl: `${url}&payment_status=cancel`,
      ...(fbEventId ? { fbEventId } : {}),
    }

    const apiEndpoint = getPaymentApi(currentPaymentMethod.value)

    try {
      const { data } = await api.post<any>(apiEndpoint, params)
      PayTemplateSuccess.value = true
      reportBeginCheckout(item.productId, 'Coins Package', item.price)
      if (data) {
        // 判断 data 是否为有效的 URL
        try {
          const _ = new URL(data)
          window.location.href = data
        }
        catch {
          isReloadPage()
        }
      }
      else {
        // 无数据时的处理逻辑
        isReloadPage()
      }
    }
    catch (error) {
      $toast($formatErrorMessage(error))
    }
    $hideLoading()
  }

  /**
   * 切换支付方式
   */
  const selectPaymentMethod = createSelectPaymentMethod(currentPaymentMethod)

  getTopUpList()

  return {
    templateProducts,
    data,
    PayTemplateSuccess,
    loading,
    PayTemplate,
    // 多支付方式相关
    currentPaymentMethod,
    availablePaymentMethods,
    currentPaymentProducts,
    selectPaymentMethod,
    productLists,
    hasPayPalPasswordlessPayment,
    hasPlacedOrder,
  }
}
